import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Star, Send, CheckCircle, AlertCircle, MessageSquare } from 'lucide-react';
import ApiService from '../services/ApiService';

const CSATSurvey = () => {
    const [searchParams] = useSearchParams();
    const queryId = searchParams.get('query_id');
    const [score, setScore] = useState(0);
    const [hover, setHover] = useState(0);
    const [comment, setComment] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState(null);
    const [orgContext, setOrgContext] = useState({
        name: 'Our Support',
        logo: null
    });
    const [isLoadingContext, setIsLoadingContext] = useState(true);

    // Effect to fetch organization context and validate queryId
    useEffect(() => {
        const fetchContext = async () => {
            if (!queryId) {
                setError('This survey link is invalid. Please use the link provided in your support email.');
                setIsLoadingContext(false);
                return;
            }

            try {
                // Try to fetch organization context for branding
                const context = await ApiService.getCSATContext(queryId);
                if (context && context.success) {
                    setOrgContext({
                        name: context.organization_name || 'Our Support',
                        logo: context.organization_logo || null
                    });
                }
            } catch (err) {
                console.warn('Could not fetch CSAT context, falling back to default branding');
            } finally {
                setIsLoadingContext(false);
            }
        };

        fetchContext();
    }, [queryId]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!queryId) return;
        if (score === 0) {
            setError('Please select a rating.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            const response = await ApiService.submitCSATScore(queryId, score, comment);
            if (response.success) {
                setSubmitted(true);
            } else {
                setError(response.message || 'Failed to submit feedback.');
            }
        } catch (err) {
            console.error('CSAT Submission Error:', err);
            // Handle specific backend error messages
            const backendMsg = err.response?.data?.message || err.response?.data?.detail;
            setError(backendMsg || 'Something went wrong. Please try again later.');
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoadingContext) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black"></div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
                <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center border border-gray-100">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-6">
                        <CheckCircle className="w-10 h-10 text-black" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h2>
                    <p className="text-gray-600">
                        Your feedback has been submitted successfully to <strong>{orgContext.name}</strong>. We appreciate your time.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                <div className="bg-white p-8 text-center border-b border-gray-50">
                    {orgContext.logo ? (
                        <img src={orgContext.logo} alt={orgContext.name} className="h-12 mx-auto mb-4 object-contain" />
                    ) : (
                        <div className="w-12 h-12 bg-black rounded-xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-gray-200">
                            <MessageSquare className="text-white w-6 h-6" />
                        </div>
                    )}
                    <h1 className="text-2xl font-black text-gray-900">How did we do?</h1>
                    <p className="text-gray-500 text-sm mt-2">Please rate your experience with <strong>{orgContext.name}</strong></p>
                </div>

                <form onSubmit={handleSubmit} className="p-8 pt-6">
                    {error && (
                        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-xl flex items-start gap-3 text-black text-sm">
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                            <p>{error}</p>
                        </div>
                    )}

                    <div className="mb-8">
                        <label className="block text-center text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">
                            Overall Satisfaction
                        </label>
                        <div className="flex justify-center gap-3">
                            {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                    key={star}
                                    type="button"
                                    onClick={() => setScore(star)}
                                    onMouseEnter={() => setHover(star)}
                                    onMouseLeave={() => setHover(0)}
                                    disabled={!queryId}
                                    className="focus:outline-none transition-all hover:scale-125 active:scale-95 disabled:opacity-30"
                                >
                                    <Star
                                        className={`w-10 h-10 ${
                                            star <= (hover || score)
                                                ? 'fill-black text-black'
                                                : 'text-gray-200'
                                        } transition-colors duration-200`}
                                    />
                                </button>
                            ))}
                        </div>
                        <div className="flex justify-between mt-4 px-2 text-[10px] text-gray-400 uppercase font-black tracking-widest">
                            <span>Disappointing</span>
                            <span>Exceptional</span>
                        </div>
                    </div>

                    <div className="mb-8">
                        <label htmlFor="comment" className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">
                            Additional Comments
                        </label>
                        <textarea
                            id="comment"
                            rows="4"
                            disabled={!queryId}
                            className="w-full px-4 py-3 rounded-xl border border-gray-100 bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-black focus:border-transparent transition-all outline-none resize-none text-gray-700 text-sm disabled:opacity-50"
                            placeholder="Tell us what stood out or how we can improve..."
                            value={comment}
                            onChange={(e) => setComment(e.target.value)}
                        ></textarea>
                    </div>

                    <button
                        type="submit"
                        disabled={isSubmitting || !queryId || score === 0}
                        className="w-full bg-gray-900 hover:bg-black text-white font-bold py-4 rounded-xl shadow-xl shadow-gray-200 transition-all flex items-center justify-center gap-2 disabled:opacity-20 disabled:cursor-not-allowed transform active:scale-[0.98]"
                    >
                        {isSubmitting ? (
                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                Submit Feedback
                            </>
                        )}
                    </button>
                </form>

                <div className="py-6 bg-gray-50/50 border-t border-gray-50 text-center">
                    <div className="flex items-center justify-center gap-2 grayscale opacity-40 hover:grayscale-0 hover:opacity-100 transition-all duration-500 cursor-default">
                        <span className="text-[10px] text-gray-400 uppercase tracking-[0.2em] font-black">
                            Powered by
                        </span>
                        <img src="/logo/uttik_logo_b.png" alt="Uttik" className="h-3" />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CSATSurvey;
